import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, CircularProgress, Alert, Box, Chip, IconButton, Tooltip, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, MenuItem, Select, FormControl, InputLabel,
    Grid, Card, CardContent, Avatar, InputAdornment, Snackbar
} from '@mui/material';
import {
    Refresh, Visibility, Cancel, LocationOn, Directions,
    DirectionsCar, CheckCircle, HourglassTop, Search, Build, Block,
    PersonAdd, SwapHoriz, Star, Phone, MyLocation, FilterList,
    History, Assignment, CheckCircleOutline
} from '@mui/icons-material';
import { customModal } from './CustomModal';

// ── Status config ─────────────────────────────────────────────
const STATUS_CONFIG = {
    scheduled: { label: 'Scheduled', color: 'default', icon: <HourglassTop fontSize="small" /> },
    searching_driver: { label: 'Searching Driver', color: 'warning', icon: <Search fontSize="small" /> },
    confirmed: { label: 'Driver Assigned', color: 'info', icon: <DirectionsCar fontSize="small" /> },
    driver_arrived: { label: 'Driver Arrived', color: 'secondary', icon: <LocationOn fontSize="small" /> },
    in_progress: { label: 'In Progress', color: 'primary', icon: <Build fontSize="small" /> },
    completed: { label: 'Completed', color: 'success', icon: <CheckCircle fontSize="small" /> },
    cancelled: { label: 'Cancelled', color: 'error', icon: <Block fontSize="small" /> },
    pending: { label: 'Pending', color: 'default', icon: <HourglassTop fontSize="small" /> },
};
const NEXT_STATUS_OPTIONS = {
    pending: ['searching_driver', 'cancelled'],
    scheduled: ['searching_driver', 'cancelled'],
    searching_driver: ['confirmed', 'cancelled'],
    confirmed: ['driver_arrived', 'cancelled'],
    driver_arrived: ['in_progress', 'cancelled'],
    in_progress: ['completed', 'cancelled'],
};

const StatusChip = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || { label: status, color: 'default', icon: null };
    return (
        <Chip
            icon={cfg.icon}
            label={cfg.label}
            color={cfg.color}
            size="small"
            sx={{ fontWeight: 600, fontSize: 11 }}
        />
    );
};

/* ── Leaflet mini-map ────────────────────────────────────────── */
const MiniMap = ({ booking }) => {
    const pickupLat = booking?.pickupLocation?.latitude;
    const pickupLng = booking?.pickupLocation?.longitude;
    const driverLat = booking?.driverLocation?.latitude;
    const driverLng = booking?.driverLocation?.longitude;

    if (!pickupLat) return <Alert severity="info">No location data.</Alert>;

    const html = `
<!DOCTYPE html><html><head>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
<style>body{margin:0}#map{height:380px}</style>
</head><body><div id="map"></div><script>
  var map = L.map('map',{zoomControl:false,attributionControl:false}).setView([${pickupLat},${pickupLng}],14);
  L.tileLayer('https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png', { maxZoom: 20, maxNativeZoom: 19 }).addTo(map);
  L.marker([${pickupLat},${pickupLng}],{
    icon:L.divIcon({html:'<div style="background:#E53935;width:16px;height:16px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>',iconSize:[16,16],iconAnchor:[8,8]})
  }).addTo(map).bindPopup('Pickup: ${(booking.pickupAddress || '').replace(/'/g, "\\'")}').openPopup();
  ${driverLat && driverLng ? `
  L.marker([${driverLat},${driverLng}],{
    icon:L.divIcon({html:'<div style="background:#1DBF73;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(29,191,115,0.6)"></div>',iconSize:[18,18],iconAnchor:[9,9]})
  }).addTo(map).bindPopup('Driver Location');
  fetch('https://router.project-osrm.org/route/v1/driving/${driverLng},${driverLat};${pickupLng},${pickupLat}?overview=full&geometries=geojson')
    .then(r=>r.json()).then(d=>{
      if(d.routes&&d.routes[0]){
        var c=d.routes[0].geometry.coordinates.map(p=>[p[1],p[0]]);
        L.polyline(c,{color:'#1DBF73',weight:5,opacity:0.7}).addTo(map);
        map.fitBounds(L.latLngBounds([[${pickupLat},${pickupLng}],[${driverLat},${driverLng}]]),{padding:[40,40]});
      }
    }).catch(()=>{});
  ` : ''}
</script></body></html>`;

    return (
        <iframe
            srcDoc={html}
            style={{ width: '100%', height: 380, border: 'none', borderRadius: 8 }}
            title="booking-map"
        />
    );
};

/* ── Driver Rating Stars ─────────────────────────────────────── */
const RatingStars = ({ rating }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
        {[1, 2, 3, 4, 5].map(s => (
            <Star key={s} sx={{ fontSize: 13, color: s <= Math.round(rating) ? '#f39c12' : '#e0e0e0' }} />
        ))}
        <Typography variant="caption" sx={{ ml: 0.3, color: 'text.secondary' }}>{Number(rating || 0).toFixed(1)}</Typography>
    </Box>
);

/* ── Assign Driver Dialog ─────────────────────────────────────── */
const AssignDriverDialog = ({ open, onClose, booking, onAssigned, isReassign = false }) => {
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [vehicleTypeFilter, setVehicleTypeFilter] = useState('');
    const [selectedDriver, setSelectedDriver] = useState(null);

    const fetchDrivers = useCallback(async () => {
        if (!booking) return;
        setLoading(true);
        setError('');
        try {
            const params = {};
            if (booking.pickupLocation?.latitude) params.lat = booking.pickupLocation.latitude;
            if (booking.pickupLocation?.longitude) params.lng = booking.pickupLocation.longitude;
            if (booking.vehicleType) params.vehicleType = booking.vehicleType;
            params.radius = 1.5; // ~165km search radius

            const res = await axios.get('/admin/available-drivers', { params });
            setDrivers(res.data.drivers || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load available drivers');
        } finally {
            setLoading(false);
        }
    }, [booking]);

    useEffect(() => {
        if (open) {
            setSelectedDriver(null);
            setError('');
            setSuccess('');
            setSearchQuery('');
            setVehicleTypeFilter('');
            fetchDrivers();
        }
    }, [open, fetchDrivers]);

    const handleAssign = async () => {
        if (!selectedDriver) return;
        setAssigning(true);
        setError('');
        try {
            const endpoint = isReassign
                ? `/admin/bookings/${booking._id}/reassign-driver`
                : `/admin/bookings/${booking._id}/assign-driver`;
            const res = await axios.post(endpoint, { driverId: selectedDriver._id });
            setSuccess(res.data.message || 'Driver assigned successfully!');
            setTimeout(() => {
                onAssigned && onAssigned(res.data.booking);
                onClose();
            }, 1200);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to assign driver. Please try again.');
        } finally {
            setAssigning(false);
        }
    };

    // Apply filters
    const filtered = drivers.filter(d => {
        const matchSearch = !searchQuery ||
            d.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            d.phoneNumber?.includes(searchQuery) ||
            d.vehicleNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            d.lastAddress?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchVehicle = !vehicleTypeFilter || d.vehicleType === vehicleTypeFilter;
        return matchSearch && matchVehicle;
    });

    const vehicleTypes = [...new Set(drivers.map(d => d.vehicleType).filter(Boolean))];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #fafafa 0%, #f0f4ff 100%)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
                }
            }}
        >
            {/* Header */}
            <DialogTitle sx={{
                background: isReassign
                    ? 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)'
                    : 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
                color: '#fff',
                pb: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {isReassign ? <SwapHoriz /> : <PersonAdd />}
                    <Box>
                        <Typography variant="h6" fontWeight={800}>
                            {isReassign ? '🔄 Reassign Driver' : '🚗 Assign Driver'}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.85 }}>
                            Booking #{String(booking?._id || '').slice(-8).toUpperCase()} — {booking?.pickupAddress?.substring(0, 50)}
                        </Typography>
                    </Box>
                </Box>
                {/* Booking meta */}
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip
                        label={`Vehicle: ${booking?.vehicleType || '—'}`}
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: 11 }}
                    />
                    <Chip
                        label={`Amount: ₹${booking?.amount || 0}`}
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: 11 }}
                    />
                    <StatusChip status={booking?.status} />
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {/* Filters bar */}
                <Box sx={{ p: 2, bgcolor: '#fff', borderBottom: '1px solid #e8ecf1', display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        placeholder="Search by name, phone, vehicle or location…"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        size="small"
                        sx={{ flex: 1, minWidth: 220 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Vehicle Type</InputLabel>
                        <Select
                            value={vehicleTypeFilter}
                            onChange={e => setVehicleTypeFilter(e.target.value)}
                            label="Vehicle Type"
                        >
                            <MenuItem value="">All Types</MenuItem>
                            {vehicleTypes.map(t => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Tooltip title="Refresh drivers list">
                        <IconButton size="small" onClick={fetchDrivers} color="primary">
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                        {filtered.length} driver{filtered.length !== 1 ? 's' : ''} available
                    </Typography>
                </Box>

                {/* Errors */}
                {error && (
                    <Alert severity="error" sx={{ m: 2, mb: 0, borderRadius: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" icon={<CheckCircleOutline />} sx={{ m: 2, mb: 0, borderRadius: 2 }}>
                        {success}
                    </Alert>
                )}

                {/* Driver list */}
                <Box sx={{ maxHeight: 440, overflowY: 'auto', p: 2 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6, flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={40} />
                            <Typography variant="body2" color="text.secondary">Loading available drivers…</Typography>
                        </Box>
                    ) : filtered.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                            <DirectionsCar sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                            <Typography variant="body1" fontWeight={500}>No available drivers found</Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>Try widening your search or check back shortly.</Typography>
                            <Button size="small" startIcon={<Refresh />} onClick={fetchDrivers} sx={{ mt: 1.5 }}>
                                Refresh List
                            </Button>
                        </Box>
                    ) : (
                        <Grid container spacing={1.5}>
                            {filtered.map(driver => {
                                const isSelected = selectedDriver?._id === driver._id;
                                return (
                                    <Grid item xs={12} key={driver._id}>
                                        <Card
                                            onClick={() => setSelectedDriver(isSelected ? null : driver)}
                                            sx={{
                                                cursor: 'pointer',
                                                border: isSelected ? '2px solid #1a237e' : '1.5px solid #e8ecf1',
                                                bgcolor: isSelected ? '#e8eaf6' : '#fff',
                                                borderRadius: 2,
                                                transition: 'all 0.18s ease',
                                                '&:hover': {
                                                    borderColor: '#1a237e',
                                                    bgcolor: '#f5f5ff',
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: '0 4px 16px rgba(26,35,126,0.12)'
                                                }
                                            }}
                                        >
                                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    {/* Avatar */}
                                                    <Avatar
                                                        sx={{
                                                            bgcolor: isSelected ? '#1a237e' : '#e8ecf1',
                                                            color: isSelected ? '#fff' : '#555',
                                                            width: 42, height: 42,
                                                            fontWeight: 700, fontSize: 16
                                                        }}
                                                    >
                                                        {driver.name?.charAt(0)?.toUpperCase() || 'D'}
                                                    </Avatar>

                                                    {/* Driver info */}
                                                    <Box sx={{ flex: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" fontWeight={700}>
                                                                {driver.name}
                                                            </Typography>
                                                            {isSelected && (
                                                                <Chip label="Selected" size="small" color="primary" sx={{ height: 18, fontSize: 10 }} />
                                                            )}
                                                        </Box>
                                                        <Box sx={{ display: 'flex', gap: 1.5, mt: 0.3, flexWrap: 'wrap' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                                <Phone sx={{ fontSize: 11, color: 'text.secondary' }} />
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {driver.phoneNumber || '—'}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                                <DirectionsCar sx={{ fontSize: 11, color: 'text.secondary' }} />
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {driver.vehicleType ? driver.vehicleType.toUpperCase() : '—'}
                                                                    {driver.vehicleNumber ? ` · ${driver.vehicleNumber}` : ''}
                                                                </Typography>
                                                            </Box>
                                                            {driver.lastAddress && (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                                    <MyLocation sx={{ fontSize: 11, color: 'text.secondary' }} />
                                                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180 }}>
                                                                        {driver.lastAddress}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                        <RatingStars rating={driver.averageRating} />
                                                    </Box>

                                                    {/* Distance badge */}
                                                    {driver.distanceKm !== null && (
                                                        <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                                                            <Typography variant="h6" fontWeight={800} color={driver.distanceKm < 5 ? 'success.main' : 'warning.main'}>
                                                                {driver.distanceKm}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">km away</Typography>
                                                        </Box>
                                                    )}

                                                    {/* Online badge */}
                                                    <Chip
                                                        label="Online"
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                        sx={{ height: 20, fontSize: 10 }}
                                                    />
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2.5, bgcolor: '#fff', borderTop: '1px solid #e8ecf1', gap: 1 }}>
                <Button onClick={onClose} variant="outlined" color="inherit" sx={{ borderRadius: 2 }}>
                    Cancel
                </Button>
                <Box sx={{ flex: 1 }} />
                {selectedDriver && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: '#1a237e', fontSize: 13, fontWeight: 700 }}>
                            {selectedDriver.name?.charAt(0)}
                        </Avatar>
                        <Typography variant="body2" fontWeight={600}>{selectedDriver.name}</Typography>
                    </Box>
                )}
                <Button
                    onClick={handleAssign}
                    variant="contained"
                    disabled={!selectedDriver || assigning}
                    startIcon={assigning ? <CircularProgress size={16} color="inherit" /> : (isReassign ? <SwapHoriz /> : <Assignment />)}
                    sx={{
                        background: isReassign
                            ? 'linear-gradient(135deg, #ff6b35, #f7931e)'
                            : 'linear-gradient(135deg, #1a237e, #283593)',
                        borderRadius: 2,
                        fontWeight: 700,
                        px: 3
                    }}
                >
                    {assigning ? 'Assigning…' : (isReassign ? 'Reassign Driver' : 'Assign Driver')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/* ── Assignment Audit Log Dialog ─────────────────────────────── */
const AuditLogDialog = ({ open, onClose, booking }) => {
    const log = booking?.assignmentLog || [];
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                <History color="primary" />
                Assignment History
            </DialogTitle>
            <DialogContent dividers>
                {log.length === 0 ? (
                    <Alert severity="info">No assignment history for this booking.</Alert>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {[...log].reverse().map((entry, idx) => (
                            <Card key={idx} variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Chip
                                            label={entry.action === 'admin_reassign' ? 'Reassigned' : 'Assigned'}
                                            size="small"
                                            color={entry.action === 'admin_reassign' ? 'warning' : 'primary'}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            {entry.assignedAt ? new Date(entry.assignedAt).toLocaleString('en-IN') : '—'}
                                        </Typography>
                                    </Box>
                                    <InfoRow label="Driver" value={entry.driverName || '—'} />
                                    <InfoRow label="Phone" value={entry.driverPhone || '—'} />
                                    <InfoRow label="Admin" value={entry.adminUsername || '—'} />
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">Close</Button>
            </DialogActions>
        </Dialog>
    );
};

/* ── Main Component ──────────────────────────────────────────── */
const BookingManagement = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [selected, setSelected] = useState(null);
    const [openView, setOpenView] = useState(false);
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [serviceOTP, setServiceOTP] = useState('');
    const [stats, setStats] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Assign driver dialog
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
    const [assignTarget, setAssignTarget] = useState(null);

    // Audit log dialog
    const [auditDialogOpen, setAuditDialogOpen] = useState(false);
    const [auditTarget, setAuditTarget] = useState(null);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const fetchBookings = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axios.get('/admin/bookings');
            const data = res.data.bookings || [];
            setBookings(data);

            const counts = {};
            data.forEach(b => { counts[b.status] = (counts[b.status] || 0) + 1; });
            setStats(counts);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load bookings');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchBookings(); }, [fetchBookings]);

    const handleView = (booking) => {
        setSelected(booking);
        setNewStatus(booking.status);
        setServiceOTP('');
        setOpenView(true);
    };

    const handleCancel = async (bookingId) => {
        const confirmed = await customModal.confirm("Cancel Booking", "Are you sure you want to cancel this booking?", { type: 'warning', confirmText: 'Cancel Booking' });
        if (!confirmed) return;
        try {
            await axios.put(`/admin/bookings/${bookingId}/status`, { status: 'cancelled' });
            fetchBookings();
            showSnackbar('Booking cancelled successfully.');
        } catch (err) {
            setError('Failed to cancel booking');
        }
    };

    const handleStatusUpdate = async () => {
        if (!selected || !newStatus) return;
        setStatusUpdating(true);
        try {
            await axios.put(`/admin/bookings/${selected._id}/status`, {
                status: newStatus,
                ...(newStatus === 'completed' ? { serviceOTP } : {}),
            });
            setOpenView(false);
            fetchBookings();
            showSnackbar('Booking status updated successfully.');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update booking status');
        } finally {
            setStatusUpdating(false);
        }
    };

    const handleOpenAssign = (booking, isReassign = false) => {
        setAssignTarget(booking);
        if (isReassign) setReassignDialogOpen(true);
        else setAssignDialogOpen(true);
    };

    const handleAssigned = (updatedBooking) => {
        fetchBookings();
        showSnackbar('Driver assigned successfully! Notifications sent to driver and customer.', 'success');
        if (openView && selected?._id === updatedBooking?._id) {
            setSelected(updatedBooking);
        }
    };

    const filtered = filterStatus === 'all'
        ? bookings
        : bookings.filter(b => b.status === filterStatus);

    /* ── STAT CARDS ─────────────────────────────────────────── */
    const statCards = [
        { label: 'Pending', key: 'pending', color: '#757575' },
        { label: 'Searching', key: 'searching_driver', color: '#f39c12' },
        { label: 'Confirmed', key: 'confirmed', color: '#2196f3' },
        { label: 'In Progress', key: 'in_progress', color: '#9c27b0' },
        { label: 'Completed', key: 'completed', color: '#4caf50' },
        { label: 'Cancelled', key: 'cancelled', color: '#f44336' },
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" fontWeight={800}>📋 Booking Management</Typography>
                <Tooltip title="Refresh">
                    <IconButton onClick={fetchBookings} color="primary" size="large">
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* ── STATS ROW ── */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ textAlign: 'center', border: '2px solid #eee' }}>
                        <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="h4" fontWeight={900}>{bookings.length}</Typography>
                            <Typography variant="caption" color="text.secondary">Total</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                {statCards.map(s => (
                    <Grid item xs={6} sm={4} md={2} key={s.key}>
                        <Card
                            sx={{ textAlign: 'center', borderTop: `4px solid ${s.color}`, cursor: 'pointer', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}
                            onClick={() => setFilterStatus(s.key)}
                        >
                            <CardContent sx={{ py: 1.5 }}>
                                <Typography variant="h4" fontWeight={900} color={s.color}>{stats[s.key] || 0}</Typography>
                                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* ── FILTER CHIPS ── */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <FilterList fontSize="small" sx={{ color: 'text.secondary' }} />
                {['all', ...Object.keys(STATUS_CONFIG)].map(s => (
                    <Chip
                        key={s}
                        label={s === 'all' ? 'All' : STATUS_CONFIG[s]?.label || s}
                        onClick={() => setFilterStatus(s)}
                        color={filterStatus === s ? 'primary' : 'default'}
                        variant={filterStatus === s ? 'filled' : 'outlined'}
                        size="small"
                    />
                ))}
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                    <CircularProgress />
                </Box>
            ) : filtered.length === 0 ? (
                <Alert severity="info">No bookings found for the selected filter.</Alert>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Booking ID</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Vehicle Type</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Pickup Address</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Driver</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map(b => (
                                <TableRow key={b._id} hover sx={{ '&:hover': { bgcolor: '#f9fbe7' } }}>
                                    <TableCell>
                                        <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                                            {String(b._id).slice(-8).toUpperCase()}
                                        </Typography>
                                        {b.assignmentLog?.length > 0 && (
                                            <Tooltip title="Has assignment history">
                                                <History sx={{ fontSize: 13, ml: 0.5, color: '#9c27b0', verticalAlign: 'middle' }} />
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell>{b.customer?.name || '—'}</TableCell>
                                    <TableCell>
                                        <Chip label={b.vehicleType || '—'} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 150 }}>
                                        <Typography variant="body2" noWrap title={b.pickupAddress}>
                                            {b.pickupAddress || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography fontWeight={700} color="success.main">₹{b.amount || 0}</Typography>
                                    </TableCell>
                                    <TableCell><StatusChip status={b.status} /></TableCell>
                                    <TableCell>
                                        {b.driver?.name ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Avatar sx={{ width: 22, height: 22, fontSize: 11, bgcolor: '#1a237e' }}>
                                                    {b.driver.name.charAt(0)}
                                                </Avatar>
                                                <Typography variant="caption">{b.driver.name}</Typography>
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" color="text.disabled">Not assigned</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">
                                            {b.createdAt ? new Date(b.createdAt).toLocaleDateString('en-IN', {
                                                day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                                            }) : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.3 }}>
                                            <Tooltip title="View Details & Map">
                                                <IconButton size="small" color="info" onClick={() => handleView(b)}>
                                                    <Visibility />
                                                </IconButton>
                                            </Tooltip>

                                            {/* Assign Driver — only for pending / searching_driver */}
                                            {['pending', 'searching_driver'].includes(b.status) && !b.driver && (
                                                <Tooltip title="Assign Driver">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenAssign(b, false)}
                                                        sx={{ color: '#1a237e' }}
                                                    >
                                                        <PersonAdd />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {/* Reassign Driver — for confirmed bookings (driver may have rejected) */}
                                            {['pending', 'searching_driver', 'confirmed'].includes(b.status) && b.driver && (
                                                <Tooltip title="Reassign Driver">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenAssign(b, true)}
                                                        sx={{ color: '#f39c12' }}
                                                    >
                                                        <SwapHoriz />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {/* Audit log */}
                                            {b.assignmentLog?.length > 0 && (
                                                <Tooltip title="View Assignment History">
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: '#9c27b0' }}
                                                        onClick={() => { setAuditTarget(b); setAuditDialogOpen(true); }}
                                                    >
                                                        <History />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {!['completed', 'cancelled'].includes(b.status) && (
                                                <Tooltip title="Cancel Booking">
                                                    <IconButton size="small" color="error" onClick={() => handleCancel(b._id)}>
                                                        <Cancel />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* ── DETAIL DIALOG ── */}
            <Dialog open={openView} onClose={() => setOpenView(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Directions color="primary" />
                    Booking Details
                    {selected && <StatusChip status={selected.status} />}
                </DialogTitle>

                <DialogContent dividers>
                    {selected && (
                        <Grid container spacing={2}>
                            {/* Info panel */}
                            <Grid item xs={12} md={5}>
                                <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>BOOKING INFO</Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <InfoRow label="Booking ID" value={selected._id} mono />
                                        <InfoRow label="Customer" value={selected.customer?.name || '—'} />
                                        <InfoRow label="Phone" value={selected.customer?.phoneNumber || '—'} />
                                        <InfoRow label="Vehicle Type" value={selected.vehicleType || selected.vehicle?.type || '—'} />
                                        <InfoRow label="Vehicle No." value={selected.vehicleNumber || selected.vehicle?.vehicleNumber || 'Not assigned'} />
                                        <InfoRow label="Driver" value={selected.driver?.name || 'Not assigned'} bold={!!selected.driver?.name} />
                                        <InfoRow label="Amount" value={`₹${selected.amount || 0}`} bold />
                                        <InfoRow label="Payment" value={selected.paymentStatus || 'pending'} />
                                        <InfoRow label="Est. Time" value={`${selected.estimatedTime || '—'} min`} />
                                        <InfoRow label="Pickup" value={selected.pickupAddress || '—'} />
                                        <InfoRow label="Created" value={selected.createdAt ? new Date(selected.createdAt).toLocaleString('en-IN') : '—'} />
                                        {selected.acceptTime && (
                                            <InfoRow label="Accepted At" value={new Date(selected.acceptTime).toLocaleString('en-IN')} />
                                        )}
                                        {selected.arrivalTime && (
                                            <InfoRow label="Arrival Time" value={`${new Date(selected.arrivalTime).toLocaleString('en-IN')}${selected.autoArrived ? ' (Auto-GPS)' : ''}`} />
                                        )}
                                        {selected.serviceStartTime && (
                                            <InfoRow label="Service Start" value={`${new Date(selected.serviceStartTime).toLocaleString('en-IN')}${selected.autoStarted ? ' (Auto-GPS)' : ''}`} />
                                        )}
                                    </Box>
                                </Box>

                                {/* Assignment actions inside detail dialog */}
                                {['pending', 'searching_driver'].includes(selected.status) && !selected.driver && (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<PersonAdd />}
                                        sx={{ mt: 1.5, background: 'linear-gradient(135deg, #1a237e, #283593)', borderRadius: 2, fontWeight: 700 }}
                                        onClick={() => { setOpenView(false); handleOpenAssign(selected, false); }}
                                    >
                                        Assign Driver to This Booking
                                    </Button>
                                )}
                                {['pending', 'searching_driver', 'confirmed'].includes(selected.status) && selected.driver && (
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<SwapHoriz />}
                                        color="warning"
                                        sx={{ mt: 1.5, borderRadius: 2, fontWeight: 700 }}
                                        onClick={() => { setOpenView(false); handleOpenAssign(selected, true); }}
                                    >
                                        Reassign to Another Driver
                                    </Button>
                                )}

                                {/* Driver GPS */}
                                {selected.driverLocation && (
                                    <Box sx={{ mt: 1.5, p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                                        <Typography variant="subtitle2" color="success.dark" gutterBottom>🟢 DRIVER GPS</Typography>
                                        <InfoRow label="Lat" value={selected.driverLocation.latitude?.toFixed(6)} mono />
                                        <InfoRow label="Lng" value={selected.driverLocation.longitude?.toFixed(6)} mono />
                                        <InfoRow label="Heading" value={`${selected.driverLocation.heading || 0}°`} />
                                    </Box>
                                )}

                                {/* Status update */}
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>Update Status</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={newStatus}
                                                onChange={e => { setNewStatus(e.target.value); setServiceOTP(''); }}
                                            >
                                                {[selected.status, ...(NEXT_STATUS_OPTIONS[selected.status] || [])]
                                                    .filter((status, index, all) => all.indexOf(status) === index)
                                                    .filter((status) => STATUS_CONFIG[status])
                                                    .map((key) => (
                                                    <MenuItem key={key} value={key}>{STATUS_CONFIG[key].label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            disabled={statusUpdating || newStatus === selected.status || (newStatus === 'completed' && serviceOTP.length !== 4)}
                                            onClick={handleStatusUpdate}
                                        >
                                            {statusUpdating ? <CircularProgress size={16} /> : 'Update'}
                                        </Button>
                                    </Box>
                                    {newStatus === 'completed' && (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Customer service OTP"
                                            value={serviceOTP}
                                            onChange={(event) => setServiceOTP(event.target.value.replace(/\D/g, '').slice(0, 4))}
                                            inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                                            sx={{ mt: 1 }}
                                        />
                                    )}
                                </Box>
                            </Grid>

                            {/* Map */}
                            <Grid item xs={12} md={7}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 1 }}>
                                    📍 LIVE MAP
                                    {selected.driverLocation
                                        ? ' — Green = Driver, Red = Pickup'
                                        : ' — Real-time driver location not yet available'}
                                </Typography>
                                <MiniMap booking={selected} />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>

                <DialogActions>
                    {selected && !['completed', 'cancelled'].includes(selected.status) && (
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={<Cancel />}
                            onClick={() => { handleCancel(selected._id); setOpenView(false); }}
                        >
                            Cancel Booking
                        </Button>
                    )}
                    <Button onClick={() => setOpenView(false)} variant="contained">Close</Button>
                </DialogActions>
            </Dialog>

            {/* ── ASSIGN DRIVER DIALOG ── */}
            <AssignDriverDialog
                open={assignDialogOpen}
                onClose={() => setAssignDialogOpen(false)}
                booking={assignTarget}
                onAssigned={handleAssigned}
                isReassign={false}
            />

            {/* ── REASSIGN DRIVER DIALOG ── */}
            <AssignDriverDialog
                open={reassignDialogOpen}
                onClose={() => setReassignDialogOpen(false)}
                booking={assignTarget}
                onAssigned={handleAssigned}
                isReassign={true}
            />

            {/* ── AUDIT LOG DIALOG ── */}
            <AuditLogDialog
                open={auditDialogOpen}
                onClose={() => setAuditDialogOpen(false)}
                booking={auditTarget}
            />

            {/* ── SNACKBAR ── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ borderRadius: 2 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

/* ── Helper ── */
const InfoRow = ({ label, value, mono, bold }) => (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80, flexShrink: 0 }}>
            {label}
        </Typography>
        <Typography
            variant="body2"
            fontWeight={bold ? 800 : 500}
            fontFamily={mono ? 'monospace' : 'inherit'}
            sx={{ wordBreak: 'break-all' }}
        >
            {value || '—'}
        </Typography>
    </Box>
);

export default BookingManagement;
